import { action } from '@/lib/api';
import debounce from '@/lib/debounce';
import { getUserCompanies, getUserCompaniesOptions, getUserState } from '@/redux/actions/userActions';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Listens for changes to the clerk user and updates redux.
 */
const UserStateHandler = () => {
  const { user: clerkUser } = useClerkUser();
  const refreshCompaniesEvent = useSelector(memoizeSelector('refreshCompaniesEvent', (state) => state?.pubSub?.refreshCompanies));
  const refreshAuthorizationEvent = useSelector((state) => state.pubSub.refreshAuthorization);
  const signupBootstrapError = useSelector((state) => state.ui.signupBootstrapError);
  const signupBootstrapRetry = useSelector((state) => state.ui.signupBootstrapRetry);
  const sessionCompanyId = useSelector((state) => state.user.sessionCompanyId);
  const lastUserProfile = useRef(null);
  const lastUserId = useRef(null);

  const profile = {
    jwt: {
      id: clerkUser?.id,
      username: clerkUser?.username,
      companyId: clerkUser?.publicMetadata?.companyId,
      email: clerkUser?.primaryEmailAddress?.emailAddress,
      alertEmail: clerkUser?.publicMetadata?.alertEmail,
      emailIsVerified: clerkUser?.primaryEmailAddress?.verification?.status === 'verified',
      name: clerkUser?.fullName,
      companyChannel: clerkUser?.publicMetadata?.companyChannel,
      adminChannel: clerkUser?.publicMetadata?.adminChannel,
      userChannel: clerkUser?.publicMetadata?.userChannel
    },
    settings: {
      name: clerkUser?.fullName,
      alertEmail: clerkUser?.publicMetadata?.alertEmail
    }
  };

  const refreshCompanies = debounce(() => {
    return new Promise((resolve) => {
      getUserCompanies({
        userId: clerkUser?.id,
        callback: (response, err) => {
          if (err) {
            if (!clerkUser?.publicMetadata?.companyId) {
              const code = err?.code || 'USER_COMPANIES_LOAD_FAILED';
              console.error('[signup-bootstrap] Failed to load or create user companies.', {
                userId: clerkUser?.id,
                code,
                statusCode: err?.statusCode
              });
              action('SET_SIGNUP_BOOTSTRAP_ERROR', {
                stage: 'userCompanies',
                code
              });
            }
            resolve(undefined);
            return;
          }

          if (signupBootstrapError?.stage === 'userCompanies') {
            action('SET_SIGNUP_BOOTSTRAP_ERROR', null);
          }
          if (response?.forceUpdate) {
            clerkUser.reload();
          }
          if (response?.companies?.length > 0) {
            getUserState({ userId: clerkUser?.id });
          }
          resolve(response);
        }
      });
    });
  }, 1000);

  useEffect(() => {
    if (profile?.jwt?.id === undefined) {
      return;
    }
    const stringifiedProfile = JSON.stringify(profile);
    if (lastUserProfile.current !== stringifiedProfile) {
      // If the user profile changed externally, refresh user companies.
      if (lastUserProfile.current !== null) {
        refreshCompanies();
      }

      // todo we have to fetch companies here
      lastUserProfile.current = stringifiedProfile;
      action('SET_USER_PROFILE', profile);
    }
  }, [profile]);

  // Fetch companies only once when user ID first becomes available
  useEffect(() => {
    if (clerkUser?.id && clerkUser.id !== lastUserId.current) {
      lastUserId.current = clerkUser.id;
      refreshCompanies();
    }
  }, [clerkUser?.id]);

  // Company authorization is supplied by the regional API, never Clerk metadata.
  useEffect(() => {
    if (clerkUser?.id && (sessionCompanyId || clerkUser.publicMetadata?.companyId)) {
      getUserState({ userId: clerkUser.id });
    }
  }, [clerkUser?.id, clerkUser?.publicMetadata?.companyId, sessionCompanyId]);

  useEffect(() => {
    const companyId = sessionCompanyId || clerkUser?.publicMetadata?.companyId;
    if (!clerkUser?.id || !companyId) {
      return;
    }
    const refresh = () => getUserState({ userId: clerkUser.id });
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [clerkUser?.id, clerkUser?.publicMetadata?.companyId, sessionCompanyId]);

  useEffect(() => {
    if (!refreshAuthorizationEvent) {
      return;
    }
    const companyId = sessionCompanyId || clerkUser?.publicMetadata?.companyId;
    if (clerkUser?.id && refreshAuthorizationEvent.companyId === companyId) {
      getUserState({ userId: clerkUser.id });
    }
    action('CLEAR_REFRESH_AUTHORIZATION');
  }, [refreshAuthorizationEvent, clerkUser?.id, clerkUser?.publicMetadata?.companyId, sessionCompanyId]);

  useEffect(() => {
    if (clerkUser?.id && signupBootstrapRetry?.stage === 'userCompanies') {
      refreshCompanies();
    }
  }, [clerkUser?.id, signupBootstrapRetry?.sequence]);

  // If the user is added as member to a new company, we receive this pub/sub message and refresh the companies
  useEffect(() => {
    if (refreshCompaniesEvent && clerkUser?.id === refreshCompaniesEvent?.userId) {
      refreshCompanies();
      getUserCompaniesOptions({ userId: clerkUser?.id });
      action('CLEAR_REFRESH_COMPANIES');
    }
  }, [refreshCompaniesEvent, clerkUser?.id]);

  return null;
};

export default UserStateHandler;
